import { useEffect, useMemo, useState } from 'react';
import { BadgeCheck, Shield, Star } from 'lucide-react';
import BookingForm from '../components/BookingForm';
import { CalendarIcon, CheckIcon, QuoteIcon } from '../components/Icons';
import { buildBookingCalendarUrl } from '../utils/calendarInvite';

const BookingPage = () => {
  const [booking, setBooking] = useState(null);

  const calendarUrl = useMemo(
    () =>
      booking
        ? buildBookingCalendarUrl({
            scheduledDate: booking.scheduledDate,
            scheduledTime: booking.scheduledTime,
            service: booking.service,
            bookingNumber: booking.bookingNumber,
          })
        : null,
    [booking]
  );

  const handleFormSuccess = (result) => {
    setBooking(result);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    document.title = 'Book Now – PaveMend';
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.href = 'https://pavemend.co.uk/booking';

    let metaDesc = document.querySelector('meta[name="description"]');
    const created = !metaDesc;
    if (created) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content =
      'Book your driveway, patio, decking or fencing installation with PaveMend, South East London’s trusted hardscaping specialists.';

    return () => {
      document.title =
        'PaveMend – Driveways, Patios & Paving in South East London';
      if (canonical) canonical.href = 'https://pavemend.co.uk/';
      if (created && metaDesc.parentNode) {
        document.head.removeChild(metaDesc);
      } else if (metaDesc) {
        metaDesc.content =
          "PaveMend is South East London's trusted hardscaping contractor. Expert driveway, patio, and paving installations with a free quote.";
      }
    };
  }, []);

  return (
    <div className="legal-page booking-page">
      <div className="legal-page__hero">
        <div className="container">
          <p className="section-eyebrow">Get Started Today</p>
          <h1 className="legal-page__title">
            Ready to transform your <em>outdoor space</em>?
          </h1>
          <p className="legal-page__meta">
            Fill in the form and we'll get back to you as soon as possible.
          </p>
        </div>
      </div>

      <div className="legal-page__body">
        <div className="container">
          {!booking && (
            <>
              <ul className="booking-page__trust-strip">
                <li>
                  <Shield aria-hidden="true" size={18} />
                  Fully insured
                </li>
                <li>
                  <BadgeCheck aria-hidden="true" size={18} />
                  3-year workmanship guarantee
                </li>
                <li>
                  <Star aria-hidden="true" size={18} />
                  No obligation, no pressure
                </li>
              </ul>

              <div className="booking-page__info">
                <QuoteIcon className="booking-page__info-icon" size={24} />
                <div className="booking-page__info-body">
                  <p className="booking-page__info-text">
                    <strong>Not ready to pick a date?</strong> Leave the date
                    and time fields blank and we'll be in touch to arrange a
                    date and time that works for you.
                  </p>
                </div>
              </div>

              <div className="booking-page__expect">
                <h2 className="booking-page__expect-title">What to expect</h2>
                <ul>
                  <li>
                    We visit your property, assess the space and talk through
                    your goals — no cost, no obligation.
                  </li>
                  <li>
                    You'll get a clear, itemized written quote afterwards —
                    materials, timeline and costs explained in plain English.
                  </li>
                  <li>
                    Need to change your date or can't make it? Just email or
                    call us and we'll happily rearrange.
                  </li>
                </ul>
              </div>
            </>
          )}

          <div className="booking-page__form-box">
            {booking ? (
              <div
                className="form-success is-active"
                role="alert"
                aria-live="polite"
              >
                <CheckIcon className="form-success__icon" size={48} />
                <h2 className="form-success__title">Booking Received!</h2>
                <p className="form-success__text">
                  Thank you for your booking request. A member of the PaveMend
                  team will be in touch shortly to confirm the details.
                </p>
                {booking.bookingNumber && (
                  <p className="form-success__reference">
                    Your reference number:{' '}
                    <strong>{booking.bookingNumber}</strong>
                  </p>
                )}

                <div className="form-success__next-steps">
                  <h3>What happens next?</h3>
                  <ol>
                    <li>
                      We'll call or email you as soon as possible to confirm
                      your visit
                      {booking.scheduledDate
                        ? ' and lock in your chosen date'
                        : ''}
                      .
                    </li>
                    <li>
                      We visit your property for a free, no-obligation
                      assessment.
                    </li>
                    <li>
                      You'll receive an itemized quote — no pressure to accept
                      on the spot.
                    </li>
                  </ol>
                </div>

                {calendarUrl && (
                  <a
                    href={calendarUrl}
                    download="pavemend-visit.ics"
                    className="form-success__calendar-link"
                  >
                    <CalendarIcon size={16} />
                    Add visit to calendar
                  </a>
                )}

                <div className="form-success__actions">
                  <a href="/#services" className="form-success__link">
                    Browse our services
                  </a>
                  <button
                    type="button"
                    className="form-success__link"
                    onClick={() => setBooking(null)}
                  >
                    Book another visit
                  </button>
                </div>
              </div>
            ) : (
              <BookingForm onSuccess={handleFormSuccess} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
